import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiLinkedin, FiSend, FiUser, FiMessageSquare, FiExternalLink } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create mailto link with form data
    const subject = encodeURIComponent(`Contact from ${formData.name}`);
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:rfkbishop@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen py-20"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-16"
        >
          <h1 className="font-poppins text-5xl font-bold text-primary mb-6">
            Get In Touch
          </h1>
          <p className="font-barlow text-lg text-tertiary max-w-2xl mx-auto">
            I'd love to hear any feedback you may have on my work and also about any potential projects you think would be a good fit for me. Let's get in touch!
          </p>
          <div className="w-24 h-1 bg-accent-primary mx-auto mt-6"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            <div>
              <h2 className="font-poppins text-2xl font-bold text-primary mb-6">
                Contact Information
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent-primary rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiMail} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow font-semibold text-primary">Email</h3>
                    <a
                      href="mailto:rfkbishop@gmail.com"
                      className="font-barlow text-accent-primary hover:text-accent-secondary transition-colors"
                    >
                      rfkbishop@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent-primary rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiLinkedin} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow font-semibold text-primary">LinkedIn</h3>
                    <a
                      href="https://www.linkedin.com/in/raheem-bishop-/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-barlow text-accent-primary hover:text-accent-secondary transition-colors"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Resume View */}
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="font-poppins text-lg font-semibold text-primary mb-4">
                Professional Resume
              </h3>
              <p className="font-barlow text-tertiary mb-4">
                View my complete professional resume
              </p>
              <a
                href="https://link.raheem.work/resume"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-primary text-white font-barlow font-medium rounded-lg hover:bg-accent-secondary transition-colors duration-300"
              >
                <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                View Resume
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100">
              <h2 className="font-poppins text-2xl font-bold text-primary mb-6">
                Send a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block font-barlow font-medium text-primary mb-2">
                    <SafeIcon icon={FiUser} className="inline w-4 h-4 mr-2" />
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all duration-200 font-barlow"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-barlow font-medium text-primary mb-2">
                    <SafeIcon icon={FiMail} className="inline w-4 h-4 mr-2" />
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all duration-200 font-barlow"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block font-barlow font-medium text-primary mb-2">
                    <SafeIcon icon={FiMessageSquare} className="inline w-4 h-4 mr-2" />
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all duration-200 font-barlow resize-none"
                    placeholder="Tell me about your project or inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-accent-primary text-white font-barlow font-medium rounded-lg hover:bg-accent-secondary transition-colors duration-300"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                  Send Message
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default Contact;